// The admin session: tokens from the passkey sign-in, kept in localStorage so
// every tab shares one session.
//
// Refresh tokens are single use and presenting a spent one again revokes the
// whole admin session (DESIGN.md "Admin sign-in"), so each change (refresh,
// sign-in, sign-out) runs inside one cross-tab Web Lock and re-reads storage
// first: a tab whose token another tab already rotated takes the new pair
// instead of replaying the old one. Refreshes are also single-flight per tab.
// A refresh whose outcome is unknown (lost response, 5xx) may have spent the
// token, so it signs out rather than risk a replay. Background polling must
// not keep a console nobody uses signed in: with `activity`, the session
// ends once 30 minutes pass without operator input (the server's idle limit,
// enforced here too), and input or a reload after that never revives it. A sign-in starts a new
// `generation`; a request that began under another one is never retried
// under the new session's token.
import type { AdminSchemas } from "@10twenty/api/admin";

export const ADMIN_SESSION_KEY = "tentwenty.admin.session.v1";
const SESSION_LOCK = "tentwenty.admin.session";
/** Refresh this long before the access token expires. */
const EXPIRY_SKEW_MS = 30_000;
const REQUEST_TIMEOUT_MS = 15_000;
/** Longest wait for another tab's change (bounded by REQUEST_TIMEOUT_MS). */
const LOCK_TIMEOUT_MS = 20_000;
/** No operator input for this long: the next refresh signs out. */
export const IDLE_LIMIT_MS = 30 * 60_000;
/** Recorded input is shared at most this often (it is written to storage). */
const ACTIVITY_GRANULARITY_MS = 15_000;

export interface StoredAdminSession {
  /** Random per sign-in; kept across refreshes. */
  generation: string;
  staff: AdminSchemas["StaffMember"];
  accessToken: string;
  /** Epoch milliseconds, as are the other times. */
  accessExpiresAt: number;
  refreshToken: string;
  refreshExpiresAt: number;
  sessionExpiresAt: number;
}

export type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface AdminSessionDeps {
  /** Admin origin plus `/api/admin/v1`. */
  baseUrl: string;
  storage: KeyValueStorage;
  fetch: (request: Request) => Promise<Response>;
  /** Cross-tab lock; without it changes are only serialized within this instance. */
  locks?: Pick<LockManager, "request"> | undefined;
  now?: () => number;
  /**
   * The operator's last input in any tab (epoch milliseconds, shared like the
   * session); omitted = always active. Null with a session counts as idle.
   */
  activity?: { get(): number | null; set(at: number): void } | undefined;
}

export function storedSession(
  generation: string,
  staff: AdminSchemas["StaffMember"],
  tokens: AdminSchemas["AdminTokens"],
): StoredAdminSession {
  return {
    generation,
    staff,
    accessToken: tokens.access_token,
    accessExpiresAt: Date.parse(tokens.access_token_expires_at),
    refreshToken: tokens.refresh_token,
    refreshExpiresAt: Date.parse(tokens.refresh_token_expires_at),
    sessionExpiresAt: Date.parse(tokens.session_expires_at),
  };
}

export function parseStoredSession(raw: string | null): StoredAdminSession | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredAdminSession> | null;
    if (
      value &&
      typeof value.generation === "string" &&
      typeof value.staff === "object" &&
      value.staff !== null &&
      typeof value.staff.user_id === "string" &&
      typeof value.staff.role === "string" &&
      typeof value.accessToken === "string" &&
      typeof value.refreshToken === "string" &&
      Number.isFinite(value.accessExpiresAt) &&
      Number.isFinite(value.refreshExpiresAt) &&
      Number.isFinite(value.sessionExpiresAt)
    ) {
      return value as StoredAdminSession;
    }
  } catch {
    // Corrupt entry: treat as signed out.
  }
  return null;
}

function withAuth(request: Request, token: string | null): Request {
  if (token === null) return request;
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return new Request(request, { headers });
}

export class AdminSessionManager {
  private readonly now: () => number;
  private readonly listeners = new Set<() => void>();
  private inflight: Promise<StoredAdminSession | null> | null = null;
  /** In-tab queue standing in for the Web Lock when it is unavailable. */
  private queue: Promise<unknown> = Promise.resolve();
  private cached: { raw: string | null; session: StoredAdminSession | null } = { raw: null, session: null };

  constructor(private readonly deps: AdminSessionDeps) {
    this.now = deps.now ?? Date.now;
  }

  /** The stored session (stable between changes, for `useSyncExternalStore`). */
  current = (): StoredAdminSession | null => {
    const raw = this.deps.storage.getItem(ADMIN_SESSION_KEY);
    if (raw !== this.cached.raw) this.cached = { raw, session: parseStoredSession(raw) };
    return this.cached.session;
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Another tab changed the stored session. */
  notifyExternalChange(): void {
    this.notify();
  }

  /** Stores a new session from sign-in or enrolment, ending the one it replaces. */
  async signIn(session: AdminSchemas["AdminSession"]): Promise<void> {
    const replaced = await this.withLock(() => {
      const previous = this.current();
      this.write(storedSession(crypto.randomUUID(), session.staff, session.tokens));
      this.deps.activity?.set(this.now());
      return previous;
    });
    this.notify();
    if (replaced && replaced.refreshToken !== session.tokens.refresh_token) await this.revoke(replaced.refreshToken);
  }

  /** Forgets the session here and in every tab, and ends it on the server. */
  async signOut(): Promise<void> {
    const previous = await this.withLock(() => {
      const stored = this.current();
      this.deps.storage.removeItem(ADMIN_SESSION_KEY);
      return stored;
    });
    this.notify();
    if (previous) await this.revoke(previous.refreshToken);
  }

  /** A current access token, refreshed when it is about to expire; null when signed out. */
  async accessToken(): Promise<string | null> {
    const stored = this.current();
    if (!stored) return null;
    // Idle: refreshLocked signs out even while the access token is still good.
    if (!this.idle() && this.now() < stored.accessExpiresAt - EXPIRY_SKEW_MS) return stored.accessToken;
    return (await this.refresh(stored))?.accessToken ?? null;
  }

  /**
   * `fetch` for the admin client: attaches the access token, and after a 401
   * refreshes once and retries. A refused refresh signs out (the role was
   * revoked, the session ended or went idle).
   */
  authFetch = async (request: Request): Promise<Response> => {
    const retry = request.clone();
    const stored = this.current();
    const token = await this.accessToken();
    const response = await this.deps.fetch(withAuth(request, token));
    if (response.status !== 401 || token === null || stored === null) return response;
    const next = await this.refresh({ generation: stored.generation, accessToken: token });
    if (!next) return response;
    const second = await this.deps.fetch(withAuth(retry, next.accessToken));
    if (second.status === 401) await this.drop(next.accessToken);
    return second;
  };

  /**
   * Rotates the pair of `seen`'s session unless it has already been rotated
   * since; null when signed out or when another sign-in replaced that session.
   */
  private refresh(seen: Pick<StoredAdminSession, "generation" | "accessToken">): Promise<StoredAdminSession | null> {
    this.inflight ??= this.withLock(() => this.refreshLocked(seen)).finally(() => {
      this.inflight = null;
    });
    return this.inflight.then((next) => (next?.generation === seen.generation ? next : null));
  }

  private async refreshLocked(
    seen: Pick<StoredAdminSession, "generation" | "accessToken">,
  ): Promise<StoredAdminSession | null> {
    const stored = this.current();
    if (!stored) return null;
    // Another sign-in replaced the session this caller used.
    if (stored.generation !== seen.generation) return stored;
    const now = this.now();
    // Another tab (or an earlier call here) already rotated it.
    if (stored.accessToken !== seen.accessToken && now < stored.accessExpiresAt - EXPIRY_SKEW_MS) return stored;
    const idle = this.idle();
    if (idle || now >= stored.refreshExpiresAt || now >= stored.sessionExpiresAt) {
      this.clearLocked();
      if (idle && now < stored.refreshExpiresAt) void this.revoke(stored.refreshToken);
      return null;
    }
    let response: Response;
    let tokens: AdminSchemas["AdminTokens"] | undefined;
    try {
      response = await this.deps.fetch(
        new Request(`${this.deps.baseUrl}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: stored.refreshToken }),
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        }),
      );
      if (response.ok) tokens = (await response.json()) as AdminSchemas["AdminTokens"];
    } catch {
      // Lost on the way back: the server may have rotated it already.
      this.clearLocked();
      void this.revoke(stored.refreshToken);
      return null;
    }
    if (tokens) {
      const next = storedSession(stored.generation, stored.staff, tokens);
      this.write(next);
      this.notify();
      return next;
    }
    // Rate limited: nothing was spent, so keep the session for the next try.
    if (response.status === 429) throw new Error("Couldn't refresh the admin session (HTTP 429)");
    // Refused (spent, idle, revoked, no longer staff) or unknown (5xx): signed
    // out. Ending it (a spent token still names its session) avoids a replay.
    this.clearLocked();
    if (response.status !== 400 && response.status !== 401) void this.revoke(stored.refreshToken);
    return null;
  }

  /**
   * Operator input (or a page load). Past the idle limit it ends the session
   * instead of extending it.
   */
  recordActivity(): void {
    const { activity } = this.deps;
    if (!activity) return;
    const stored = this.current();
    if (stored && this.idle()) {
      void this.expireIdle(stored.generation);
      return;
    }
    const now = this.now();
    const last = activity.get();
    if (last === null || now - last >= ACTIVITY_GRANULARITY_MS) activity.set(now);
  }

  private idle(): boolean {
    const { activity } = this.deps;
    if (!activity) return false;
    const last = activity.get();
    return last === null || this.now() - last >= IDLE_LIMIT_MS;
  }

  private async expireIdle(generation: string): Promise<void> {
    const ended = await this.withLock(() => {
      const stored = this.current();
      if (stored?.generation !== generation || !this.idle()) return null;
      this.clearLocked();
      return stored;
    });
    if (ended && this.now() < ended.refreshExpiresAt) await this.revoke(ended.refreshToken);
  }

  /** Signs out locally if `accessToken` is still the stored one. */
  private async drop(accessToken: string): Promise<void> {
    await this.withLock(() => {
      if (this.current()?.accessToken === accessToken) this.clearLocked();
    });
  }

  private clearLocked(): void {
    this.deps.storage.removeItem(ADMIN_SESSION_KEY);
    this.notify();
  }

  private async revoke(refreshToken: string): Promise<void> {
    try {
      await this.deps.fetch(
        new Request(`${this.deps.baseUrl}/auth/sign-out`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        }),
      );
    } catch {
      // Best effort: the session still ends with its idle and absolute limits.
    }
  }

  private write(session: StoredAdminSession): void {
    this.deps.storage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private withLock<T>(task: () => T | Promise<T>): Promise<T> {
    const { locks } = this.deps;
    if (locks) {
      return locks.request(SESSION_LOCK, { signal: AbortSignal.timeout(LOCK_TIMEOUT_MS) }, task) as Promise<T>;
    }
    const run = this.queue.then(task, task);
    this.queue = run.catch(() => undefined);
    return run;
  }
}
